"use client"

import styled from "@emotion/styled"
import { FineDetailsPublicResponse, PaymentSetupPublicResponse } from "api"
import { useSearchParams } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import Container from "ui/Container"
import { extractProperty } from "utils"
import Loading from "../Common/Loading"
import Error from "./Error"
import FirstStage from "./FirstStage/FirstStage"
import SecondStage from "./SecondStage/SecondStage"
import Stepper from "./Stepper"
import ThirdStage from "./ThirdStage/ThirdStage"

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* max-width: 1120px; */
  padding: 0 16px;
  padding-top: 32px;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 0;
    padding-top: 48px;
    gap: 48px;
  }
`

const InnerContainer = styled.div`
  max-width: 1120px;
`

export type ErrorType =
  | "invalidForm"
  | "alreadyPaid"
  | "notPayable"
  | "iframeError"
  | "inProcess"
  | "cancelled"

export default function Payment({ finePayment }: { finePayment: any }) {
  const searchParams = useSearchParams()
  const resetToken = searchParams.get("reset")
  const stepsBlock = extractProperty(finePayment, "finePaymentSteps") || []
  const steps = stepsBlock.map((el: any) => ({
    stepName: extractProperty(el, "stepName"),
    icon: extractProperty(el, "icon"),
    title: extractProperty(el, "title"),
    subTitle: extractProperty(el, "subTitle"),
  })) as {
    stepName: string
    icon: string
    title: string
    subTitle: string
  }[]
  const [step, setStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<ErrorType | undefined>()
  const [paymentData, setPaymentData] = useState<PaymentSetupPublicResponse>()
  const [data, setData] = useState<FineDetailsPublicResponse>()
  const thirdStageTitleRef = useRef<HTMLHeadingElement>(null)

  const toSecondStage = () => {
    setStep(2)
  }
  const toThirdStage = () => {
    setStep(3)
    // Focus will be set in ThirdStage component's useEffect
  }

  useEffect(() => {
    if (resetToken) {
      setStep(1)
      setIsLoading(false)
      setError(undefined)
      setPaymentData(undefined)
      setData(undefined)
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior })
      }
    }
  }, [resetToken])
  return (
    <Container>
      <StyledContainer>
        <Stepper step={step} steps={steps} />
        <InnerContainer>
          {error ? (
            <Error
              setError={setError}
              setStep={setStep}
              error={error}
              data={data!}
            />
          ) : isLoading ? (
            <Loading />
          ) : (
            <>
              {step === 1 && (
                <FirstStage
                  toSecondStage={toSecondStage}
                  setIsLoading={setIsLoading}
                  setData={setData}
                  stepData={steps[0]}
                  setError={setError}
                />
              )}
              {step === 2 && (
                <SecondStage
                  setIsLoading={setIsLoading}
                  toThirdStage={toThirdStage}
                  setPaymentData={setPaymentData}
                  data={data!}
                  stepData={steps[1]}
                  setError={setError}
                />
              )}
              {step === 3 && (
                <ThirdStage
                  data={data!}
                  paymentData={paymentData!}
                  stepData={steps[2]}
                  setError={setError}
                  titleRef={thirdStageTitleRef}
                />
              )}
            </>
          )}
        </InnerContainer>
      </StyledContainer>
    </Container>
  )
}
